import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, MapPin, Wine, Leaf, ForkKnife, Info, MagnifyingGlass } from '@phosphor-icons/react';
import { getGrapeVarietyInfoUrl } from '../../utils/grapeVarietyUtils';
import { Breadcrumb } from '../Product/Breadcrumb';
import { useToast } from '../../context/ToastContext';

// Import all subregion data functions
import { getRiojaAltaData } from '../../data/subregions/rioja-alta';
import { getRiberaAltaData } from '../../data/subregions/ribera-alta';
import { getCimaCorgoData } from '../../data/subregions/cima-corgo';
import { getAlentejoCentralData } from '../../data/subregions/alentejo-central';
import { getSerraDaEstrelaData } from '../../data/subregions/serra-da-estrela';
import { getMoncaoMelgacoData } from '../../data/subregions/moncao-melgaco';
import { getChiantiClassicoData } from '../../data/subregions/chianti-classico';
import { getLangheData } from '../../data/subregions/langhe';
import { getValpolicellaData } from '../../data/subregions/valpolicella';
import { getBolgheriData } from '../../data/subregions/bolgheri';
import { getMaremmaData } from '../../data/subregions/maremma';
import { getArboisData } from '../../data/subregions/arbois';
import { getChateauChalonData } from '../../data/subregions/chateau-chalon';
import { getLEtoileData } from '../../data/subregions/l-etoile';
import { getWairauValleyData } from '../../data/subregions/wairau-valley';
import { getGimblettGravelsData } from '../../data/subregions/gimblett-gravels';
import { getBannockburnData } from '../../data/subregions/bannockburn';
import { getMartinboroughData } from '../../data/subregions/martinborough';
import { getGladstoneData } from '../../data/subregions/gladstone';
import { getMastertonData } from '../../data/subregions/masterton';
import { getWaiparaValleyData } from '../../data/subregions/waipara-valley';
import { getNorthCanterburyData } from '../../data/subregions/north-canterbury';
import { getCentralCanterburyData } from '../../data/subregions/central-canterbury';
import { getMoutereHillsData } from '../../data/subregions/moutere-hills';
import { getWaimeaPlainsData } from '../../data/subregions/waimea-plains';
import { getTasmanBayData } from '../../data/subregions/tasman-bay';
import { getSudsteiermarkData } from '../../data/subregions/sudsteiermark';
import { getWeststeiermarkData } from '../../data/subregions/weststeiermark';
import { getOststeiermarkData } from '../../data/subregions/oststeiermark';
import { getNussbergData } from '../../data/subregions/nussberg';
import { getBisambergData } from '../../data/subregions/bisamberg';
import { getMauerData } from '../../data/subregions/mauer';
import { getCahorsData } from '../../data/subregions/cahors';
import { getBergeracData } from '../../data/subregions/bergerac';
import { getMadiranData } from '../../data/subregions/madiran';
import { getJuranconData } from '../../data/subregions/jurancon';
import { getMedocData } from '../../data/subregions/medoc';
import { getSaintEmilionData } from '../../data/subregions/saint_emilion';
import { getGravesData } from '../../data/subregions/graves';
import { getBandolData } from '../../data/subregions/bandol';
import { getCotesProvenceData } from '../../data/subregions/cotes_provence';
import { getCassisData } from '../../data/subregions/cassis';
import { getLenswoodData } from '../../data/subregions/lenswood';
import { getPenolaData } from '../../data/subregions/penola';
import { getClareData } from '../../data/subregions/clare';
import { getUpperYarraValleyData } from '../../data/subregions/upper-yarra-valley';
import { getBlewittSpringsData } from '../../data/subregions/blewitt-springs';
import { getWilyabrupData } from '../../data/subregions/wilyabrup';
import { getLowerHunterValleyData } from '../../data/subregions/lower-hunter-valley';
import { getHighEdenData } from '../../data/subregions/high-eden';
import { getEdenValleyData } from '../../data/subregions/eden-valley';
import { getChateauneufDuPapeData } from '../../data/subregions/chateauneuf-du-pape';
import { getCoteRotieData } from '../../data/subregions/cote-rotie';
import { getHermitageData } from '../../data/subregions/hermitage';
import { getCondrieuData } from '../../data/subregions/condrieu';
import { getCornasData } from '../../data/subregions/cornas';
import { getSaintJosephData } from '../../data/subregions/saint-joseph';
import { getVacqueyrasData } from '../../data/subregions/vacqueyras';
import { getGigondasData } from '../../data/subregions/gigondas';

// Import region data functions
import { getProvenceData } from '../../data/regions/provence';
import { getBordeauxData } from '../../data/regions/bordeaux';
import { getRhoneData } from '../../data/regions/rhone';
import { getRiojaData } from '../../data/regions/rioja';
import { getRiberaDelDueroData } from '../../data/regions/ribera-del-duero';
import { getNavarraData } from '../../data/regions/navarra';
import { getSudOuestData } from '../../data/regions/sud-ouest';

// Subregion data mapping
const subregionDataMap = {
  'rioja-alta': getRiojaAltaData,
  'ribera-alta': getRiberaAltaData,
  'cima-corgo': getCimaCorgoData,
  'alentejo-central': getAlentejoCentralData,
  'serra-da-estrela': getSerraDaEstrelaData,
  'moncao-melgaco': getMoncaoMelgacoData,
  'chianti-classico': getChiantiClassicoData,
  'langhe': getLangheData,
  'valpolicella': getValpolicellaData,
  'bolgheri': getBolgheriData,
  'maremma': getMaremmaData,
  'arbois': getArboisData,
  'chateau-chalon': getChateauChalonData,
  'l-etoile': getLEtoileData,
  'wairau-valley': getWairauValleyData,
  'gimblett-gravels': getGimblettGravelsData,
  'bannockburn': getBannockburnData,
  'martinborough': getMartinboroughData,
  'gladstone': getGladstoneData,
  'masterton': getMastertonData,
  'waipara-valley': getWaiparaValleyData,
  'north-canterbury': getNorthCanterburyData,
  'central-canterbury': getCentralCanterburyData,
  'moutere-hills': getMoutereHillsData,
  'waimea-plains': getWaimeaPlainsData,
  'tasman-bay': getTasmanBayData,
  'sudsteiermark': getSudsteiermarkData,
  'weststeiermark': getWeststeiermarkData,
  'oststeiermark': getOststeiermarkData,
  'nussberg': getNussbergData,
  'bisamberg': getBisambergData,
  'mauer': getMauerData,
  'cahors': getCahorsData,
  'bergerac': getBergeracData,
  'madiran': getMadiranData,
  'jurancon': getJuranconData,
  'medoc': getMedocData,
  'saint_emilion': getSaintEmilionData,
  'graves': getGravesData,
  'bandol': getBandolData,
  'cotes_provence': getCotesProvenceData,
  'cassis': getCassisData,
  'lenswood': getLenswoodData,
  'penola': getPenolaData,
  'clare': getClareData,
  'upper-yarra-valley': getUpperYarraValleyData,
  'blewitt-springs': getBlewittSpringsData,
  'wilyabrup': getWilyabrupData,
  'lower-hunter-valley': getLowerHunterValleyData,
  'high-eden': getHighEdenData,
  'eden-valley': getEdenValleyData,
  'chateauneuf-du-pape': getChateauneufDuPapeData,
  'cote-rotie': getCoteRotieData,
  'hermitage': getHermitageData,
  'condrieu': getCondrieuData,
  'cornas': getCornasData,
  'saint-joseph': getSaintJosephData,
  'vacqueyras': getVacqueyrasData,
  'gigondas': getGigondasData,
};

// Region data mapping
const regionDataMap = {
  'provence': getProvenceData,
  'bordeaux': getBordeauxData,
  'rhone': getRhoneData,
  'rioja': getRiojaData,
  'ribera-del-duero': getRiberaDelDueroData,
  'navarra': getNavarraData,
  'sud-ouest': getSudOuestData,
};

export const GrapeVarietiesPage = () => {
  const { country, region, subregion } = useParams();
  const navigate = useNavigate();
  const [subregionData, setSubregionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // First try to get subregion data
    const getSubregionData = subregionDataMap[subregion];
    const getRegionData = regionDataMap[region];
    
    if (getSubregionData) {
      try {
        const data = getSubregionData();
        setSubregionData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading subregion data:', error);
        setError('Erreur lors du chargement des données.');
        setLoading(false);
      }
    } else if (getRegionData) {
      try {
        const data = getRegionData();
        setSubregionData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading region data:', error);
        setError('Erreur lors du chargement des données.');
        setLoading(false);
      }
    } else {
      setError('Appellation non trouvée');
      setLoading(false);
    }
  }, [subregion, region]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }

  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }

  if (!subregionData) return null;

  // Extract grape varieties from subregion data
  const getGrapeVarieties = () => {
    const varieties = [];
    
    // Handle array of grape variety objects
    if (Array.isArray(subregionData.grapeVarieties)) {
      subregionData.grapeVarieties.forEach(variety => {
        // Determine if it's a red or white variety based on name and characteristics
        const isRedVariety = isRedGrapeVariety(variety.name);
        const isWhiteVariety = isWhiteGrapeVariety(variety.name);
        
        varieties.push({
          name: variety.name,
          percentage: variety.percentage,
          description: variety.description,
          characteristics: variety.characteristics,
          wineStyles: variety.wineStyles,
          type: isRedVariety ? 'red' : isWhiteVariety ? 'white' : 'detailed'
        });
      });
    }
    
    // Handle object with color keys
    if (typeof subregionData.grapeVarieties === 'object' && !Array.isArray(subregionData.grapeVarieties)) {
      Object.entries(subregionData.grapeVarieties).forEach(([color, varietyList]) => {
        if (Array.isArray(varietyList)) {
          varietyList.forEach(variety => {
            varieties.push({
              name: variety,
              type: color,
              percentage: 'Variable',
              description: `Cépage ${color} cultivé dans ${subregionData.name}`,
              characteristics: `Caractéristiques typiques des cépages ${color} de cette appellation`,
              wineStyles: `Styles de vin ${color} caractéristiques de ${subregionData.name}`
            });
          });
        }
      });
    }
    
    return varieties;
  };

  // Helper function to identify red grape varieties
  const isRedGrapeVariety = (name) => {
    const redVarieties = [
      'grenache', 'syrah', 'mourvèdre', 'cinsault', 'carignan', 'counoise', 'muscardin', 'vaccarèse', 'terret noir', 'picardan',
      'cabernet sauvignon', 'cabernet franc', 'merlot', 'malbec', 'petit verdot', 'pinot noir', 'nebbiolo', 'sangiovese',
      'barbera', 'dolcetto', 'corvina', 'rondinella', 'molinara', 'tempranillo', 'garnacha', 'graciano', 'mazuelo',
      'touriga nacional', 'touriga franca', 'tinta roriz', 'tinta barroca', 'baga', 'encruzado', 'agiorgitiko',
      'blaufränkisch', 'zweigelt', 'saint-laurent', 'pinotage', 'shiraz', 'zinfandel', 'petite sirah', 'carignane'
    ];
    return redVarieties.includes(name.toLowerCase());
  };

  // Helper function to identify white grape varieties
  const isWhiteGrapeVariety = (name) => {
    const whiteVarieties = [
      'grenache blanc', 'roussanne', 'marsanne', 'viognier', 'clairette', 'bourboulenc', 'picpoul', 'muscat blanc',
      'chardonnay', 'sauvignon blanc', 'semillon', 'riesling', 'pinot gris', 'pinot grigio', 'gewürztraminer',
      'chenin blanc', 'muscadet', 'aligoté', 'pinot blanc', 'auxerrois', 'garganega', 'cortese', 'trebbiano',
      'vermentino', 'fiano', 'greco', 'falanghina', 'albariño', 'alvarinho', 'verdejo', 'godello', 'loureiro',
      'trajadura', 'arinto', 'encruzado', 'baga', 'fernão pires', 'malvasia', 'moschofilero', 'roditis', 'assyrtiko',
      'grüner veltliner', 'welschriesling', 'muscat', 'müller-thurgau', 'silvaner', 'kerner', 'scheurebe'
    ];
    return whiteVarieties.includes(name.toLowerCase());
  };

  const grapeVarieties = getGrapeVarieties();
  const filteredVarieties = grapeVarieties.filter(variety =>
    variety.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGrapeVarietyClick = (grapeVariety) => {
    // Check if this grape variety has individual data available
    const hasIndividualData = checkGrapeVarietyDataExists(grapeVariety.name, country);
    
    if (hasIndividualData) {
      const infoUrl = getGrapeVarietyInfoUrl(grapeVariety.name, country, region, subregion);
      if (infoUrl) {
        navigate(infoUrl);
      }
    } else {
      // Show a message that individual data is not available
      showToast(
        `Informations détaillées pour ${grapeVariety.name} ne sont pas encore disponibles. Les informations affichées ici sont les plus complètes actuellement.`,
        'warning',
        5000
      );
    }
  };

  // Helper function to check if grape variety data exists
  const checkGrapeVarietyDataExists = (grapeVarietyName, country) => {
    const normalizedName = grapeVarietyName.toLowerCase().replace(/\s+/g, '-');
    const countryLower = country.toLowerCase();
    
    // List of grape varieties that have individual data files
    const availableVarieties = {
      'france': [
        'grenache', 'syrah', 'mourvedre', 'cinsault', 'carignan', 'petit-verdot', 
        'cabernet-sauvignon', 'merlot', 'chardonnay', 'sauvignon-blanc', 'pinot-noir',
        'poulsard', 'trousseau', 'savagnin', 'counoise', 'muscardin', 'vaccarese', 
        'terret-noir', 'picardan', 'grenache-blanc', 'roussanne', 'clairette', 'bourboulenc', 'viognier'
      ],
      'spain': [
        'tempranillo', 'garnacha', 'graciano', 'albariño', 'verdejo'
      ],
      'italy': [
        'sangiovese', 'nebbiolo', 'cabernet-franc', 'merlot', 'cabernet-sauvignon',
        'nero-davola', 'pinot-grigio'
      ],
      'portugal': [
        'touriga-nacional', 'alvarinho', 'baga', 'encruzado'
      ],
      'austria': [
        'gruner-veltliner', 'zweigelt', 'blaufrankisch', 'riesling'
      ],
      'argentina': [
        'malbec', 'chardonnay'
      ],
      'australia': [
        'shiraz'
      ],
      'new-zealand': [
        'pinot-noir', 'sauvignon-blanc'
      ]
    };
    
    const countryVarieties = availableVarieties[countryLower] || [];
    return countryVarieties.includes(normalizedName);
  };

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>
            Cépages de {subregionData.name}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <MapPin size={16} />
            <span>{region} - {country}</span>
            {subregion && subregion !== region && <span> - {subregion}</span>}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ position: 'relative', maxWidth: '500px' }}>
          <MagnifyingGlass 
            size={20} 
            color="#666" 
            style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} 
          />
          <input
            type="text"
            placeholder="Rechercher un cépage..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '16px 16px 16px 48px',
              border: '2px solid #e8e0e8',
              borderRadius: '12px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4B164C'}
            onBlur={(e) => e.target.style.borderColor = '#e8e0e8'}
          />
        </div>
      </div>

      {/* Grape Varieties Grid */}
      {filteredVarieties.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
          {filteredVarieties.map((variety, index) => (
            <div
              key={index}
              onClick={() => handleGrapeVarietyClick(variety)}
              style={{
                background: '#fff',
                border: '2px solid #e8e0e8',
                borderRadius: '16px',
                padding: '24px',
                cursor: checkGrapeVarietyDataExists(variety.name, country) ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                opacity: checkGrapeVarietyDataExists(variety.name, country) ? 1 : 0.8
              }}
              onMouseOver={e => {
                if (checkGrapeVarietyDataExists(variety.name, country)) {
                  e.currentTarget.style.borderColor = '#4B164C';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(75,22,76,0.15)';
                }
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = '#e8e0e8';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Type Badge */}
              {variety.type && variety.type !== 'detailed' && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: variety.type === 'red' ? '#dc2626' : variety.type === 'white' ? '#fbbf24' : '#4B164C',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {variety.type}
                </div>
              )}

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: '#4B164C',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🍇
                </div>
                <div>
                  <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '600' }}>
                    {variety.name}
                  </h3>
                  {variety.percentage && (
                    <span style={{
                      background: '#f0e6f0',
                      color: '#4B164C',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {variety.percentage}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <p style={{ fontSize: '14px', color: '#333', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                {variety.description}
              </p>

              {/* Characteristics */}
              {variety.characteristics && (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>
                    Caractéristiques spécifiques à {subregionData.name}:
                  </h4>
                  <p style={{ fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                    {variety.characteristics}
                  </p>
                </div>
              )}

              {/* Wine Styles */}
              {variety.wineStyles && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>
                    Styles de vin:
                  </h4>
                  <p style={{ fontSize: '13px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                    {variety.wineStyles}
                  </p>
                </div>
              )}

              {/* Terroir Context */}
              <div style={{
                background: '#f8f6fa',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e8e0e8'
              }}>
                <h4 style={{ fontSize: '13px', color: '#4B164C', margin: '0 0 6px 0', fontWeight: '600' }}>
                  Terroir de {subregionData.name}:
                </h4>
                <p style={{ fontSize: '12px', color: '#666', margin: '0', lineHeight: '1.4' }}>
                  {subregionData.characteristics?.climate && `Climat: ${subregionData.characteristics.climate}. `}
                  {subregionData.characteristics?.soil && `Sols: ${subregionData.characteristics.soil}. `}
                  {subregionData.characteristics?.viticulture && `Viticulture: ${subregionData.characteristics.viticulture}`}
                </p>
              </div>

              {/* Click indicator */}
              {checkGrapeVarietyDataExists(variety.name, country) && (
                <div style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  color: '#4B164C',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  →
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          background: '#f8f6fa',
          borderRadius: '16px',
          padding: '48px 32px',
          textAlign: 'center',
          border: '2px dashed #e8e0e8'
        }}>
          <Wine size={48} color="#4B164C" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600' }}>
            Aucun cépage trouvé
          </h3>
          <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>
            Aucun cépage ne correspond à votre recherche "{searchTerm}"
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {grapeVarieties.length > 0 && (
        <div style={{
          background: '#f8f6fa',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '32px',
          border: '1px solid #e8e0e8'
        }}>
          <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
            Résumé des Cépages de {subregionData.name}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#4B164C', fontWeight: '700' }}>{grapeVarieties.length}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>Cépages cultivés</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#4B164C', fontWeight: '700' }}>
                {grapeVarieties.filter(v => v.type === 'red').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Cépages rouges</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', color: '#4B164C', fontWeight: '700' }}>
                {grapeVarieties.filter(v => v.type === 'white').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Cépages blancs</div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
